import { PureComponent } from 'react';

// React router components
import { withRouter } from 'react-router-dom';

// Validation helper
import validate from 'validate.js';

// Snackbar provider
import { withSnackbar } from 'notistack';

import config from 'config';

// Firebase
import firebase from 'firebase/app';
import 'firebase/firebase-storage';

// Import material components
import {
  Avatar,
  Button,
  IconButton,
  TextField,
  Link,
  FormHelperText,
  Checkbox,
  Grow,
  CircularProgress,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  withStyles,
  Grid
} from '@material-ui/core';

// Date picker
import { DatePicker } from '@material-ui/pickers';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

// DateTime for testing
// import { DateTime } from 'luxon';

// Autocomplete
import { Autocomplete } from '@material-ui/lab';

// To handle error messages
import { getErrorMessage, getInitials, countries, uuid } from 'helpers';

// Authentication provider
import { withAuth } from 'services/auth';

// Cleanup exports
import compose from 'recompose/compose';

// Upload component
import { Upload } from 'components';

// Component styles
import styles from './styles';

// Import the schema
import schema from './schema';

const capitalize = (input) => {
  if (typeof input === 'string' && input.length > 0)
    return input.charAt(0).toUpperCase() + input.slice(1);
};

// English Language Tests
const ENGLISH_TESTS = [
  'IELTS General',
  'IELTS Academic',
  'PTE Academic',
  'PTE Core',
  'PTE Home',
  'TOEFL iBT',
  'OET (Occupational English Test)',
  'Michigan English Test (MET)',
  'Cambridge C1 Advanced',
  'Duolingo English Test',
  'CELPIP (Canadian English Language Proficiency Index Program)',
  'LANGUAGECERT International ESOL SELT',
  'LANGUAGECERT Academic SELT',
  'LANGUAGECERT General SELT',
  'PSI Services: Skills for English UKVI',
  'Trinity College London: Secure English Language Tests for UKVI',
  'Trinity College London: Integrated Skills in English (ISE)',
  'Trinity College London: Graded Examinations in Spoken English (GESE)',
  'B2 First'
];

class Application extends PureComponent {
  constructor(props) {
    super(props);

    // Setup a uuid for the application
    this.uuid = uuid();

    // Initialize the component's state
    this.state = {
      isValid: false,
      status: undefined,
      values: {
        gender: 'Male',
        englishTestScores: [{ test: '', score: '' }] // Initialize with one empty entry
      },
      touched: {},
      errors: validate({}, schema)
    };

    // Bind the required functions
    this.handleProfileUpload = this.handleProfileUpload.bind(this);
    this.handleCVUpload = this.handleCVUpload.bind(this);
    this.handleDegreeUpload = this.handleDegreeUpload.bind(this);
    this.handleEclUpload = this.handleEclUpload.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleBack = this.handleBack.bind(this);
    this.hasError = this.hasError.bind(this);
    this.handleAddEnglishTest = this.handleAddEnglishTest.bind(this);
    this.handleRemoveEnglishTest = this.handleRemoveEnglishTest.bind(this);
    this.handleEnglishTestChange = this.handleEnglishTestChange.bind(this);

    // Initialize the ready flag
    this.ready = true;
  }

  componentWillUnmount() {
    this.ready = false;
  }
  validateYouTubeURL(url) {
    if (!url) return true;
    const youtubePatterns = [
      /^https?:\/\/(www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})(&.*)?$/,
      /^https?:\/\/(www\.)?youtu\.be\/([a-zA-Z0-9_-]{11})(\?.*)?$/,
      /^https?:\/\/(www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]{11})(\?.*)?$/,
      /^([a-zA-Z0-9_-]{11})$/ // Just the video ID
    ];
    return youtubePatterns.some((pattern) => pattern.test(url));
  }
  handleChange(event) {
    // Grab the values from the state
    const { values, touched } = this.state;

    // Get the newly changed values
    const newValues = {
      ...values,
      [event.target.name]:
        event.target.type === 'checkbox'
          ? event.target.checked
          : event.target.value
    };

    // Validate the state's values
    let errors = validate(newValues, schema);

    // Additional YouTube URL validation
    if (event.target.name === 'video' && newValues.video) {
      if (!this.validateYouTubeURL(newValues.video)) {
        errors = errors || {};
        errors.video = [
          'Please enter a valid YouTube URL (youtube.com/watch?v=, youtu.be/, or just the video ID)'
        ];
      }
    }

    // Update the state
    this.setState({
      values: newValues,
      touched: {
        ...touched,
        [event.target.name]: true
      },
      isValid: !errors,
      errors: errors || {}
    });
  }

  handleBack() {
    // Grab the history provider from the props
    const { history } = this.props;

    // Navigate back
    history.push('/signin');
  }

  async handleProfileUpload(image) {
    try {
      // Get the download URL of the image
      const imageURL = await firebase.storage().ref(image).getDownloadURL();

      // Update the state with the image
      if (this.ready) {
        this.handleChange({
          target: {
            name: 'image',
            value: imageURL
          }
        });
      }
    } catch (error) {
      // Get the enqueueSnackbar
      const { enqueueSnackbar } = this.props;

      // Show an error snackbar
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    }
  }

  async handleCVUpload(cv) {
    try {
      // Get the download URL of the image
      const cvURL = await firebase.storage().ref(cv).getDownloadURL();

      // Update the state with the image
      if (this.ready) {
        this.handleChange({
          target: {
            name: 'cv',
            value: cvURL
          }
        });
      }
    } catch (error) {
      // Get the enqueueSnackbar
      const { enqueueSnackbar } = this.props;

      // Show an error snackbar
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    }
  }
  async handleDegreeUpload(degree) {
    try {
      // Get the download URL of the image
      const degreeURL = await firebase.storage().ref(degree).getDownloadURL();

      // Update the state with the image
      if (this.ready) {
        this.handleChange({
          target: {
            name: 'degree',
            value: degreeURL
          }
        });
      }
    } catch (error) {
      // Get the enqueueSnackbar
      const { enqueueSnackbar } = this.props;

      // Show an error snackbar
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    }
  }
  async handleEclUpload(ecl) {
    try {
      // Get the download URL of the image
      const eclURL = await firebase.storage().ref(ecl).getDownloadURL();

      // Update the state with the image
      if (this.ready) {
        this.handleChange({
          target: {
            name: 'ecl',
            value: eclURL
          }
        });
      }
    } catch (error) {
      // Get the enqueueSnackbar
      const { enqueueSnackbar } = this.props;

      // Show an error snackbar
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    }
  }

  async handleSubmit(event) {
    // Prevent an actual form submit from occuring
    event.preventDefault();

    // Grab the auth provider from the props and state info
    const { isValid, values } = this.state;
    const { auth, enqueueSnackbar } = this.props;

    // Check whether the sign up form is valid
    if (isValid) {
      try {
        // Disable the form elements
        this.setState({ status: 'submitting' });

        // Filter out empty English test scores (where test or score is empty)
        console.log('Original englishTestScores:', values.englishTestScores);
        const filteredTestScores = (values.englishTestScores || []).filter(
          (testScore) => {
            if (!testScore || typeof testScore !== 'object') {
              console.log('Invalid test score object:', testScore);
              return false;
            }

            const hasTest =
              testScore.test &&
              typeof testScore.test === 'string' &&
              testScore.test.trim() !== '';
            const hasScore =
              testScore.score &&
              typeof testScore.score === 'string' &&
              testScore.score.trim() !== '';

            const isValid = hasTest && hasScore;
            console.log('Checking test score:', {
              test: testScore.test,
              score: testScore.score,
              hasTest,
              hasScore,
              isValid
            });
            return isValid;
          }
        );

        // Prepare application data
        const applicationData = {
          ...values,
          firstName: capitalize(values.firstName),
          lastName: capitalize(values.lastName),
          dob: values.dob.ts,
          nationality: values.nationality.label,
          country: values.country.label,
          currency: 'PHP',
          englishTestScores:
            filteredTestScores.length > 0 ? filteredTestScores : undefined
        };

        console.log('Submitting application with data:', applicationData);
        console.log('Filtered English Test Scores:', filteredTestScores);

        // Submit the application
        await auth.api.applications.submitApplication(applicationData);

        // Update the state
        if (this.ready) {
          this.setState({ status: 'submitted' });
        }
      } catch (error) {
        // Log the error to the console
        console.error(error);

        // Re-enable the form elements
        if (this.ready) {
          this.setState({ status: undefined, isValid: false });

          // Show an error snackbar
          enqueueSnackbar(getErrorMessage(error), {
            variant: 'error'
          });
        }
      }
    }
  }

  hasError(field) {
    // Grab the touched and errors state info
    const { touched, errors } = this.state;
    return !!(touched[field] && errors[field]);
  }

  handleAddEnglishTest() {
    const { values } = this.state;
    const englishTestScores = values.englishTestScores || [];

    this.setState({
      values: {
        ...values,
        englishTestScores: [...englishTestScores, { test: '', score: '' }]
      }
    });
  }

  handleRemoveEnglishTest(index) {
    const { values } = this.state;
    const englishTestScores = [...(values.englishTestScores || [])];
    englishTestScores.splice(index, 1);

    this.setState({
      values: {
        ...values,
        englishTestScores
      }
    });
  }

  handleEnglishTestChange(index, field, value) {
    const { values } = this.state;
    const englishTestScores = [...(values.englishTestScores || [])];
    englishTestScores[index] = {
      ...englishTestScores[index],
      [field]: value
    };

    this.setState({
      values: {
        ...values,
        englishTestScores
      }
    });
  }

  render() {
    // Grab the component's classes from its props
    const { classes } = this.props;

    // Grab the state info
    const { status, isValid, errors, values } = this.state;

    const isProdBranch = config.IS_PROD_BRANCH;

    const bucketName = isProdBranch
      ? 'eng-tutor-app.appspot.com'
      : 'eng-tutor-app-dev.appspot.com';

    if (status === 'submitted') {
      return (
        <div className={classes.rootHome}>
          <Grow in>
            <div className={classes.contentSubmitted}>
              <img
                alt=""
                src="images/connected_world.svg"
                className={classes.image}
              />
              <Typography variant="h2" className={classes.title}>
                Application Submitted
              </Typography>
              <Typography color="textSecondary" variant="h5">
                Thanks for submitting you application! Applications are
                typically reviewed and processed within 3-5 business days,
                however this is dependent on the volume of applications we
                receive.
                <br />
                <br />
                You will receive an email notifying you of your
                application&apos;s outcome once it has been processed.
              </Typography>
              <div className={classes.home}>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.homeButton}
                  onClick={this.handleBack}>
                  awesome
                </Button>
              </div>
            </div>
          </Grow>
        </div>
      );
    }

    // Render the sign up page
    return (
      <div className={classes.root}>
        <div className={classes.content}>
          <form className={classes.form} onSubmit={this.handleSubmit}>
            <div className={classes.formHeader}>
              <IconButton
                className={classes.backButton}
                onClick={this.handleBack}
                disabled={status === 'submitting'}>
                <ArrowBackIcon />
              </IconButton>
              <div>
                <Typography variant="h2">Teacher Application</Typography>
                <Typography className={classes.subtitle} gutterBottom>
                  Thanks for taking an interest in becoming a teacher for
                  e-English.
                </Typography>
              </div>
              <span className={classes.spacer} />
              <Grow in={status === 'submitting'}>
                <div>
                  <CircularProgress />
                </div>
              </Grow>
            </div>
            <Typography className={classes.subheading} gutterBottom>
              To complete this form you will need to have ready to upload a
              profile photo, your CV (pdf), your Bachelor Degree Certificate
              (pdf), copies of all your English Language Certificates (in a
              single pdf), and a short YouTube video. Read through the form for
              instructions about these requirements.
            </Typography>
            <div className={classes.formProfile}>
              <Avatar className={classes.avatar} src={values.image}>
                {getInitials(
                  (values.firstName || '') + (values.lastName || '')
                )}
              </Avatar>
            </div>
            <div style={{ marginTop: '1.5rem' }}>
              <Upload
                disabled={status === 'submitting'}
                label="Upload profile photo (<1MB)"
                path={`profiles/${this.uuid}`}
                onUploadSuccess={this.handleProfileUpload}
                maxSize={1024 * 1024}
                accept="image/*"
                customizedStyle={true}
              />
            </div>
            <div style={{ marginTop: '0.5rem' }}>
              <Upload
                disabled={status === 'submitting'}
                label="Upload CV (<1MB)"
                path={`cv/${this.uuid}`}
                onUploadSuccess={this.handleCVUpload}
                maxSize={1024 * 1024}
                accept="application/pdf"
              />
            </div>
            <div style={{ marginTop: '0.5rem' }}>
              <Upload
                disabled={status === 'submitting'}
                label="Upload Bachelor Degree Certificate (<1MB)"
                path={`degree/${this.uuid}`}
                onUploadSuccess={this.handleDegreeUpload}
                maxSize={1024 * 1024}
                accept="application/pdf"
                bachelorDegreeCertificateText={true}
              />
            </div>
            <div style={{ marginTop: '0.5rem' }}>
              <Upload
                disabled={status === 'submitting'}
                label="Upload English Language Certificate (<1MB)"
                path={`ecl/${this.uuid}`}
                onUploadSuccess={this.handleEclUpload}
                maxSize={1024 * 1024}
                accept="application/pdf"
              />
            </div>
            <Typography variant="h4" style={{ marginTop: '1rem' }}>
              Firstly, your details
            </Typography>
            <Typography className={classes.subtitle}>
              Only your name will be publicly visible.
            </Typography>

            <TextField
              className={classes.textField}
              error={this.hasError('firstName')}
              fullWidth
              helperText={
                this.hasError('firstName') ? errors.firstName[0] : null
              }
              label="First Name"
              name="firstName"
              onChange={this.handleChange}
              type="text"
              value={values.firstName || ''}
              variant="outlined"
              disabled={status === 'submitting'}
            />
            <TextField
              className={classes.textField}
              error={this.hasError('lastName')}
              fullWidth
              helperText={this.hasError('lastName') ? errors.lastName[0] : null}
              label="Last Name"
              name="lastName"
              onChange={this.handleChange}
              type="text"
              value={values.lastName || ''}
              variant="outlined"
              disabled={status === 'submitting'}
            />
            <DatePicker
              value={values.dob || null}
              format="dd/MM/yyyy"
              margin="normal"
              disableFuture
              views={['year', 'month', 'date']}
              label="Date Of Birth"
              onChange={(dateTime) => {
                this.handleChange({
                  target: {
                    name: 'dob',
                    value: dateTime
                  }
                });
              }}
              renderInput={(props) => (
                <TextField
                  fullWidth
                  variant="outlined"
                  className={classes.textField}
                  error={this.hasError('dob')}
                  helperText={this.hasError('dob') ? errors.dob[0] : null}
                  disabled={status === 'submitting'}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...props}
                />
              )}
            />
            <TextField
              className={classes.textField}
              error={this.hasError('email')}
              fullWidth
              helperText={this.hasError('email') ? errors.email[0] : null}
              label="Email"
              name="email"
              onChange={this.handleChange}
              type="text"
              value={values.email || ''}
              variant="outlined"
              disabled={status === 'submitting'}
            />
            <TextField
              className={classes.textField}
              error={this.hasError('password')}
              fullWidth
              helperText={this.hasError('password') ? errors.password[0] : null}
              label="Password"
              name="password"
              onChange={this.handleChange}
              type="password"
              value={values.password || ''}
              variant="outlined"
              disabled={status === 'submitting'}
            />
            <TextField
              className={classes.textField}
              error={this.hasError('confirmPassword')}
              fullWidth
              helperText={
                this.hasError('confirmPassword')
                  ? 'Please enter the same password as above'
                  : null
              }
              label="Confirm Password"
              name="confirmPassword"
              onChange={this.handleChange}
              type="password"
              value={values.confirmPassword || ''}
              variant="outlined"
              disabled={status === 'submitting'}
            />
            <Typography variant="h4" className={classes.header}>
              Tell us a bit about yourself
            </Typography>
            <Typography className={classes.subtitle}>
              This information will be visible to students
            </Typography>
            <FormControl
              variant="outlined"
              className={classes.textField}
              fullWidth>
              <Select
                id="application-select-gender"
                value={values.gender || 'Male'}
                name="gender"
                onChange={this.handleChange}>
                <MenuItem value="Male">Male</MenuItem>
                {/* eslint-disable-next-line react/jsx-boolean-value */}
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
            <Autocomplete
              autoHighlight
              options={countries}
              getOptionLabel={(option) => option.label}
              className={classes.textField}
              onChange={(event, change) => {
                this.handleChange({
                  target: {
                    name: 'country',
                    value: change
                  }
                });
              }}
              renderInput={(params) => (
                <TextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  label="Country"
                  name="country"
                  variant="outlined"
                  error={this.hasError('country')}
                  helperText={
                    this.hasError('country') ? errors.country[0] : null
                  }
                  fullWidth
                />
              )}
            />
            <Autocomplete
              autoHighlight
              options={countries}
              getOptionLabel={(option) => option.label}
              className={classes.textField}
              onChange={(event, change) => {
                this.handleChange({
                  target: {
                    name: 'nationality',
                    value: change
                  }
                });
              }}
              renderInput={(params) => (
                <TextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  label="Nationality"
                  name="nationality"
                  variant="outlined"
                  error={this.hasError('nationality')}
                  helperText={
                    this.hasError('nationality') ? errors.nationality[0] : null
                  }
                  fullWidth
                />
              )}
            />
            <TextField
              className={classes.textField}
              fullWidth
              label="List the Languages you are fluent in"
              name="languages"
              onChange={this.handleChange}
              type="text"
              value={values.languages || ''}
              variant="outlined"
              disabled={status === 'submitting'}
            />
            {/* English Test Scores Section */}
            <div className={classes.textField}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                English Language Test Scores (Optional)
              </Typography>

              {(values.englishTestScores || []).map((testScore, index) => (
                <Grid
                  container
                  spacing={2}
                  key={index}
                  style={{ marginBottom: '1rem' }}>
                  <Grid item xs={12} sm={6}>
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel>Select English Test</InputLabel>
                      <Select
                        value={testScore.test || ''}
                        onChange={(e) =>
                          this.handleEnglishTestChange(
                            index,
                            'test',
                            e.target.value
                          )
                        }
                        label="Select English Test"
                        disabled={status === 'submitting'}>
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {ENGLISH_TESTS.map((test) => (
                          <MenuItem key={test} value={test}>
                            {test}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      label="Score"
                      value={testScore.score || ''}
                      onChange={(e) =>
                        this.handleEnglishTestChange(
                          index,
                          'score',
                          e.target.value
                        )
                      }
                      variant="outlined"
                      placeholder="Enter your score"
                      disabled={status === 'submitting'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    {index > 0 && (
                      <IconButton
                        onClick={() => this.handleRemoveEnglishTest(index)}
                        color="secondary"
                        disabled={status === 'submitting'}
                        size="small">
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              ))}

              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<AddIcon />}
                onClick={this.handleAddEnglishTest}
                disabled={status === 'submitting'}>
                Add Another Test
              </Button>
            </div>
            <TextField
              className={classes.textField}
              error={this.hasError('school')}
              fullWidth
              helperText={this.hasError('school') ? errors.school[0] : null}
              label="Graduating School"
              name="school"
              onChange={this.handleChange}
              type="text"
              value={values.school || ''}
              variant="outlined"
              disabled={status === 'submitting'}
            />
            <div style={{ marginTop: '0.5rem' }}>
              <Typography className={classes.subheadingIntro}>
                <strong>
                  Do you have experience in any of the following teaching or
                  professional areas? Please check all the boxes that apply.
                </strong>
              </Typography>
              <Grid container mt={2}>
                <Grid item xs={4}>
                  <div className={classes.policy}>
                    <Checkbox
                      checked={values.pteTeaching || false}
                      color="primary"
                      name="pteTeaching"
                      onChange={this.handleChange}
                      disabled={status === 'submitting'}
                    />
                    <Typography variant="body1">PTE Teaching</Typography>
                  </div>
                </Grid>
                <Grid item xs={4}>
                  <div className={classes.policy}>
                    <Checkbox
                      checked={values.teslTefl || false}
                      color="primary"
                      name="teslTefl"
                      onChange={this.handleChange}
                      disabled={status === 'submitting'}
                    />
                    <Typography variant="body1">TESL/TEFL</Typography>
                  </div>
                </Grid>
                <Grid item xs={4}>
                  <div className={classes.policy}>
                    <Checkbox
                      checked={values.tesol || false}
                      color="primary"
                      name="tesol"
                      onChange={this.handleChange}
                      disabled={status === 'submitting'}
                    />
                    <Typography variant="body1">TESOL</Typography>
                  </div>
                </Grid>
                <Grid item xs={4}>
                  <div className={classes.policy}>
                    <Checkbox
                      checked={values.celta || false}
                      color="primary"
                      name="celta"
                      onChange={this.handleChange}
                      disabled={status === 'submitting'}
                    />
                    <Typography variant="body1">CELTA</Typography>
                  </div>
                </Grid>
                <Grid item xs={4}>
                  <div className={classes.policy}>
                    <Checkbox
                      checked={values.ieltsGeneralTraining || false}
                      color="primary"
                      name="ieltsGeneralTraining"
                      onChange={this.handleChange}
                      disabled={status === 'submitting'}
                    />
                    <Typography variant="body1">
                      IELTS General Training
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={4}>
                  <div className={classes.policy}>
                    <Checkbox
                      checked={values.health || false}
                      color="primary"
                      name="health"
                      onChange={this.handleChange}
                      disabled={status === 'submitting'}
                    />
                    <Typography variant="body1">Health</Typography>
                  </div>
                </Grid>
                <Grid item xs={4}>
                  <div className={classes.policy}>
                    <Checkbox
                      checked={values.hospitality || false}
                      color="primary"
                      name="hospitality"
                      onChange={this.handleChange}
                      disabled={status === 'submitting'}
                    />
                    <Typography variant="body1">Hospitality</Typography>
                  </div>
                </Grid>
                <Grid item xs={4}>
                  <div className={classes.policy}>
                    <Checkbox
                      checked={values.tourism || false}
                      color="primary"
                      name="tourism"
                      onChange={this.handleChange}
                      disabled={status === 'submitting'}
                    />
                    <Typography variant="body1">Tourism</Typography>
                  </div>
                </Grid>
              </Grid>
            </div>

            <Typography className={classes.subheadingIntro}>
              Your
              <strong>
                &nbsp;Short Introduction is an opportunity to introduce yourself
                to students&nbsp;
              </strong>
              and your achievements, including the following:
            </Typography>
            <Typography component="div">
              <ul className={classes.bulletContainer}>
                <li className={classes.subtitle}>
                  Your Teaching experience and passion for teaching English
                </li>
                <li className={classes.subtitle}>
                  Your educational experience
                </li>
                <li
                  className={
                    classes.subtitle
                  }>{`Your work Study Area experience (eg Hospitality, Tourism etc)`}</li>
              </ul>
            </Typography>
            <TextField
              className={classes.textField}
              placeholder="Short introduction about why you want to be a tutor for e-English, and why you would be a good tutor."
              error={this.hasError('introduction')}
              fullWidth
              helperText={
                this.hasError('introduction') ? errors.introduction[0] : null
              }
              label="Short Introduction"
              name="introduction"
              onChange={this.handleChange}
              type="text"
              value={values.introduction || ''}
              variant="outlined"
              disabled={status === 'submitting'}
              multiline
              rows={4}
            />
            <Typography className={classes.subheadingIntro}>
              Develop a 30 sec (maximum)
              <strong>&nbsp;Introduction Video&nbsp;</strong>
              based on your Short Introduction.The video will need to have
              <strong>&nbsp;Unlisted or Public visibility.&nbsp;</strong>
            </Typography>
            <TextField
              className={classes.textField}
              error={this.hasError('video')}
              fullWidth
              helperText={
                this.hasError('video')
                  ? errors.video[0]
                  : 'Enter full YouTube URL (youtube.com/watch?v=VIDEO_ID or youtu.be/VIDEO_ID) or just the video ID'
              }
              label="YouTube Video URL"
              name="video"
              onChange={this.handleChange}
              type="text"
              value={values.video || ''}
              variant="outlined"
              disabled={status === 'submitting'}
              placeholder="https://www.youtube.com/watch?v=aqz-KE-bpKQ or aqz-KE-bpKQ"
            />

            {/* <TextField
              className={classes.textField}
              error={this.hasError('video')}
              fullWidth
              helperText={this.hasError('video') ? errors.video[0] : null}
              label="YouTube Video URL"
              name="video"
              onChange={this.handleChange}
              type="text"
              value={values.video || ''}
              variant="outlined"
              disabled={status === 'submitting'}
            /> */}
            <Typography className={classes.intructions}>
              {/* eslint-disable */}
              Instructions for{' '}
              <Link
                href="https://support.google.com/youtube/answer/57407"
                target="_blank">
                uploading to YouTube
              </Link>{' '}
              and{' '}
              <Link
                href="https://gist.github.com/jakebellacera/d81bbf12b99448188f183141e6696817"
                target="_blank">
                retrieving your video ID
              </Link>
              {/* eslint-enable */}
            </Typography>
            <div className={classes.policy}>
              <Checkbox
                checked={values.tutorPolicy || false}
                className={classes.policyCheckbox}
                color="primary"
                name="tutorPolicy"
                onChange={this.handleChange}
                disabled={status === 'submitting'}
              />
              <Typography
                className={classes.policyText}
                color="textSecondary"
                variant="body1">
                {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                I have read the{' '}
              </Typography>
              <Typography
                onClick={() =>
                  window
                    .open(
                      `https://firebasestorage.googleapis.com/v0/b/${bucketName}/o/tutorapplicationterms%2FTeacher%20Application%20Terms.pdf?alt=media&token=3281a587-6f6a-4b24-9e54-c0de2619f1ef`,
                      '_blank'
                    )
                    .focus()
                }
                className="policy-text"
                variant="h6"
                style={{ marginLeft: 5 }}>
                Teacher Application Terms
              </Typography>
            </div>
            {this.hasError('tutorPolicy') && (
              <FormHelperText error>{errors.tutorPolicy[0]}</FormHelperText>
            )}
            <div className={classes.policyBottom}>
              <Checkbox
                checked={values.policy || false}
                className={classes.policyCheckbox}
                color="primary"
                name="policy"
                onChange={this.handleChange}
                disabled={status === 'submitting'}
              />
              <Typography
                className={classes.policyText}
                color="textSecondary"
                variant="body1">
                {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                I have read the e-English app
              </Typography>
              <Typography
                onClick={() =>
                  window
                    .open('https://e-english.com.au/terms-of-use/', '_blank')
                    .focus()
                }
                className="policy-text"
                variant="h6"
                style={{ marginLeft: 5 }}>
                Terms and Conditions
              </Typography>
            </div>
            {this.hasError('policy') && (
              <FormHelperText error>{errors.policy[0]}</FormHelperText>
            )}
            <div className={classes.applyErrors}>
              {Object.entries(errors)
                .slice(0, 3)
                .map(([key, error]) => (
                  <Typography
                    className={classes.applyErrorText}
                    variant="subtitle2"
                    key={key}>
                    {error}
                  </Typography>
                ))}
            </div>
            <Button
              className={classes.applyButton}
              color="primary"
              disabled={!isValid || status === 'submitting'}
              fullWidth
              size="large"
              type="submit"
              variant="contained">
              Apply Now
            </Button>
          </form>
        </div>
      </div>
    );
  }
}

export default compose(
  withAuth,
  withRouter,
  withSnackbar,
  withStyles(styles)
)(Application);
